import { useState } from "react";
import { AiTag, Button, Field, Icon, Menu, Notice } from "@omnabase/ui";
import { TRY_SAMPLES } from "@omnabase/shared";

// Try it (M5 round two), the prototype's aside: a sample ticket (two, from shared), or a pasted real ticket (choice 18), Run, and the
// draft the AI would offer with its confidence, what it followed and what it cited. Nothing is sent; the run holds the unsaved text.
export type RunResult = { body: string; confidence: number; sources: { id: string; title: string; href?: string }[]; followed: string[] };

export function TryInstructions({ canPaste, waiting, result, onRun, onClose }: { canPaste: boolean; waiting: boolean; result: RunResult | null; onRun: (body: { sample?: string; text?: string }) => void; onClose: () => void }) {
  const [sample, setSample] = useState<(typeof TRY_SAMPLES)[number]>(TRY_SAMPLES[0]);
  const [pasting, setPasting] = useState(false);
  const [pasted, setPasted] = useState("");
  const [ran, setRan] = useState(0);
  const run = () => { setRan((n) => n + 1); onRun(pasting ? { text: pasted.trim() } : { sample: sample.id }); };
  return (
    <aside className="ob-try" aria-label="Try it">
      <div className="ob-try__head">Try it<span style={{ marginLeft: "auto" }}><Button variant="icon" size="sm" icon="x" aria-label="Close" onClick={onClose} /></span></div>
      <div className="ob-try__body">
        {pasting ? (
          <>
            <span className="ob-label">Customer wrote</span>
            <textarea className="ob-instr__text" aria-label="A real ticket" placeholder="Paste the customer's message" value={pasted} onChange={(e) => setPasted(e.target.value)} maxLength={20_000} />
          </>
        ) : (
          <>
            <Menu trigger={<Field label="Sample" value={`Sample: ${sample.label}`} />} sections={[{ label: "Sample ticket", items: TRY_SAMPLES.map((x) => ({ id: x.id, label: x.label, checked: x.id === sample.id, onSelect: () => setSample(x) })) }]} />
            <div className="ob-try__box"><span className="ob-label">Customer wrote</span>{sample.text}</div>
          </>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="primary" size="sm" disabled={waiting || (pasting && !pasted.trim())} onClick={run}>{ran ? "Run again" : "Run"}</Button>
          {canPaste && <Button size="sm" active={pasting} onClick={() => setPasting((v) => !v)}>{pasting ? "Sample ticket" : "Paste a real ticket"}</Button>}
        </div>
        {waiting && <AiTag generating>Writing…</AiTag>}
        {result && !waiting && <>
          <span className="ob-try__label"><Icon name="sparkles" size="sm" />Draft the AI would offer<span style={{ marginLeft: "auto", fontWeight: 400, color: "var(--ob-text-muted)" }}>Confidence {Math.round(result.confidence * 100)} %</span></span>
          <div className="ob-try__box"><div style={{ whiteSpace: "pre-wrap" }}>{result.body}</div><span className="ob-try__meta">Followed: {result.followed.length ? result.followed.join(" · ") : "nothing in particular"}</span><span className="ob-try__meta" style={{ borderTop: 0, paddingTop: 0 }}>Cited: {result.sources.length ? result.sources.map((s) => s.title).join(", ") : "no source"}</span></div>
          <Notice icon="info">Nothing was sent. This runs the unsaved text, so edit a line and run again to see what changes.</Notice>
        </>}
      </div>
    </aside>
  );
}
